import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteTaxRateSchedulesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteTaxRateSchedulesTypeahead';
import { toTaxRateScheduleOption } from '~components/FormFields/CommonAutocompletes/toTaxRateScheduleOption';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import { TaxRateScheduleTypeahead } from '~hooks/useTaxRateSchedule/models';
import useTaxRateScheduleQuery from '~queries/useTaxRateScheduleQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

const I18N_PREFIX =
  'administration.integration.rutter.quick_books_online.advanced_configuration.default_untaxed_tax_rate_schedule';

type TaxRateScheduleOption = AutocompleteFieldOption<TaxRateScheduleTypeahead>;

interface DefaultUntaxedTaxRateScheduleFormValues {
  taxRateSchedule: Nullable<TaxRateScheduleOption>;
}

interface DefaultUntaxedTaxRateScheduleSettingProps {
  companyId: string;
  companySettings: CompanySettings;
}

export default function DefaultUntaxedTaxRateScheduleSetting({
  companyId,
  companySettings,
}: DefaultUntaxedTaxRateScheduleSettingProps) {
  const { toasterStore } = useStores();
  const { updateCompanySettings } = useCompanySettings();
  const [isSaving, setIsSaving] = useState(false);
  const { isLookupError, taxRateSchedule: savedTaxRateSchedule } =
    useTaxRateScheduleQuery({
      companyId,
      taxRateScheduleId: companySettings.defaultUntaxedTaxRateScheduleId,
    });
  const { control, getValues, setValue, watch } =
    useForm<DefaultUntaxedTaxRateScheduleFormValues>({
      defaultValues: { taxRateSchedule: null },
    });
  // The last value the server accepted, or undefined while the saved schedule is still loading.
  // A failed save rolls back to it, and it is tracked here because a managed company's
  // settings prop is not refreshed after a save.
  const acceptedOptionRef = useRef<Nullable<TaxRateScheduleOption> | undefined>(
    undefined,
  );

  // Once the user has picked, the form owns the value: a lookup resolving afterwards must not
  // overwrite a newer pick.
  const hasPickedRef = useRef(false);

  useEffect(() => {
    // a failed lookup must not blank a schedule the server still holds
    const lookupFailedForSetSchedule =
      !savedTaxRateSchedule && companySettings.defaultUntaxedTaxRateScheduleId !== null;
    if (lookupFailedForSetSchedule) {
      return;
    }

    const savedOption = savedTaxRateSchedule
      ? toTaxRateScheduleOption(savedTaxRateSchedule.toTypeahead())
      : null;

    if (hasPickedRef.current) {
      acceptedOptionRef.current ??= savedOption;

      return;
    }

    acceptedOptionRef.current = savedOption;
    setValue('taxRateSchedule', savedOption);
  }, [
    savedTaxRateSchedule?.id,
    companySettings.defaultUntaxedTaxRateScheduleId,
    setValue,
  ]);

  const handleSelect = async (nextOption: Nullable<TaxRateScheduleOption>) => {
    hasPickedRef.current = true;
    setIsSaving(true);

    try {
      await updateCompanySettings(companyId, {
        default_untaxed_tax_rate_schedule_id: nextOption?.value ?? null,
      });
      acceptedOptionRef.current = nextOption;
      toasterStore.push(
        alert(t('administration.configuration.configuration_saved'), AlertTypes.success),
      );
    } catch {
      // the saved schedule never loaded, so let it hydrate the field when it does
      if (acceptedOptionRef.current === undefined) {
        hasPickedRef.current = false;
      }
      setValue('taxRateSchedule', acceptedOptionRef.current ?? null);
    } finally {
      setIsSaving(false);
    }
  };
  // the subscription below is created once, so it reads the latest handler through this ref
  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;

  // Only a user's pick has type "change"; resetting the value with setValue does not.
  useEffect(() => {
    const subscription = watch((_formValues, { type }) => {
      if (type === 'change') {
        void handleSelectRef.current(getValues('taxRateSchedule'));
      }
    });

    return () => subscription.unsubscribe();
  }, [getValues, watch]);

  const lookupFailedWithEmptyField = isLookupError && !watch('taxRateSchedule');

  return (
    <Box data-test-id="default-untaxed-tax-rate-schedule-setting" sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
        {t(`${I18N_PREFIX}.title`)}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        {t(`${I18N_PREFIX}.description`)}
      </Typography>
      <Box sx={{ maxWidth: '420px' }}>
        <AutocompleteTaxRateSchedulesTypeahead
          companyId={companyId}
          control={control}
          isDisabled={isSaving}
          name="taxRateSchedule"
        />
        {lookupFailedWithEmptyField && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t(`${I18N_PREFIX}.lookup_failed`)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
